import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import './WorkerActiveCountBadge.scss';

const LOCAL_QUEBANK_SERVER_URL = 'http://localhost:8010';
const PRODUCTION_QUEBANK_SERVER_URL = 'https://que.xn--vl2b19ao0ssqc39r.kr/api';
const LOCAL_UTIL_SERVER_URL = 'http://localhost:8009';
const PRODUCTION_UTIL_SERVER_URL = 'https://util.xn--vl2b19ao0ssqc39r.kr/api';

function isLocalHostname() {
  return (
    typeof window !== 'undefined'
    && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  );
}

function getQueBankServerUrls() {
  const configuredUrl = String(process.env.REACT_APP_QUEBANK_SERVER_URL || '').trim();
  if (configuredUrl) {
    return [configuredUrl.replace(/\/$/, '')];
  }

  if (isLocalHostname()) {
    return [LOCAL_QUEBANK_SERVER_URL, PRODUCTION_QUEBANK_SERVER_URL];
  }

  return [PRODUCTION_QUEBANK_SERVER_URL];
}

function getUtilServerUrls() {
  const configuredUrl = String(process.env.REACT_APP_UTIL_SERVER_URL || '').trim();
  if (configuredUrl) {
    return [configuredUrl.replace(/\/$/, '')];
  }

  if (isLocalHostname()) {
    return [LOCAL_UTIL_SERVER_URL, PRODUCTION_UTIL_SERVER_URL];
  }

  return [PRODUCTION_UTIL_SERVER_URL];
}

function getActiveWorkerCount(response) {
  if (!response?.data?.success) return null;

  const count = Number(response.data.active_worker_count);
  return Number.isFinite(count) ? count : 0;
}

async function requestActiveCounts(serverUrl) {
  const requestOptions = { timeout: 5000 };
  const requests = [
    axios.post(`${serverUrl}/creatDbRoutes/queueSummary`, {
      user_id: '',
      worker_id: '',
      work_kind: 'CREATE_DB_FROM_HWP'
    }, requestOptions),
    axios.post(`${serverUrl}/creatDbRoutes/queueSummary`, {
      user_id: '',
      worker_id: '',
      work_kind: 'PRINT'
    }, requestOptions)
  ];

  const [createDbResult, printResult] = await Promise.allSettled(requests);

  return {
    createDb: createDbResult.status === 'fulfilled'
      ? getActiveWorkerCount(createDbResult.value)
      : null,
    print: printResult.status === 'fulfilled'
      ? getActiveWorkerCount(printResult.value)
      : null
  };
}

async function requestUtilActiveCount(serverUrl) {
  try {
    const response = await axios.get(
      `${serverUrl}/hwp2pngRoutes/workerAvailability`,
      { timeout: 5000 }
    );

    if (!response?.data?.success) return null;

    const count = Number(response.data.activeCount ?? response.data.active_count);
    return Number.isFinite(count) ? count : 0;
  } catch {
    return null;
  }
}

const WorkerActiveCountBadge = ({ className = '' }) => {
  const [activeCounts, setActiveCounts] = useState({
    createDb: null,
    print: null,
    util: null
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasError, setHasError] = useState(false);
  const lastZeroAlertKeyRef = useRef('');

  const refreshActiveCounts = useCallback(async () => {
    setIsRefreshing(true);
    setHasError(false);

    const queBankCountsPromise = (async () => {
      let counts = { createDb: null, print: null };

      for (const serverUrl of getQueBankServerUrls()) {
        counts = await requestActiveCounts(serverUrl);
        if (counts.createDb !== null && counts.print !== null) {
          break;
        }
      }

      return counts;
    })();

    const utilCountPromise = (async () => {
      let count = null;

      for (const serverUrl of getUtilServerUrls()) {
        count = await requestUtilActiveCount(serverUrl);
        if (count !== null) {
          break;
        }
      }

      return count;
    })();

    const [queBankCounts, utilCount] = await Promise.all([
      queBankCountsPromise,
      utilCountPromise
    ]);
    const nextCounts = {
      ...queBankCounts,
      util: utilCount
    };
    const zeroServiceLabels = [
      nextCounts.createDb === 0 ? '디비' : '',
      nextCounts.print === 0 ? '프린트' : '',
      nextCounts.util === 0 ? 'util' : ''
    ].filter(Boolean);
    const zeroAlertKey = zeroServiceLabels.join('|');

    if (zeroAlertKey && lastZeroAlertKeyRef.current !== zeroAlertKey) {
      window.alert(`${zeroServiceLabels.join(', ')} 활성 서버가 0개입니다.`);
      lastZeroAlertKeyRef.current = zeroAlertKey;
    } else if (!zeroAlertKey) {
      lastZeroAlertKeyRef.current = '';
    }

    setActiveCounts(nextCounts);
    setHasError(
      nextCounts.createDb === null
      || nextCounts.print === null
      || nextCounts.util === null
    );
    setIsRefreshing(false);
  }, []);

  useEffect(() => {
    refreshActiveCounts();
  }, [refreshActiveCounts]);

  const createDbDisplay = activeCounts.createDb === null
    ? '-'
    : activeCounts.createDb.toLocaleString();
  const printDisplay = activeCounts.print === null
    ? '-'
    : activeCounts.print.toLocaleString();
  const utilDisplay = activeCounts.util === null
    ? '-'
    : activeCounts.util.toLocaleString();

  return (
    <div
      className={`worker-active-count-wrap ${hasError ? 'has-error' : ''} ${className}`.trim()}
      title={hasError ? '일부 활성 서버 수를 조회하지 못했습니다.' : 'work 기준 활성 서버 수'}
    >
      <p className="worker-active-count" aria-live="polite">
        <span className="worker-active-label">디비</span>{' '}
        <strong className="worker-active-value">{createDbDisplay}</strong>
        <span className="worker-active-separator"> · </span>
        <span className="worker-active-label">프린트</span>{' '}
        <strong className="worker-active-value">{printDisplay}</strong>
        <span className="worker-active-separator"> · </span>
        <span className="worker-active-label">util</span>{' '}
        <strong className="worker-active-value">{utilDisplay}</strong>
      </p>
      <button
        type="button"
        className={`worker-active-refresh ${isRefreshing ? 'is-refreshing' : ''}`}
        onClick={refreshActiveCounts}
        disabled={isRefreshing}
      >
        {isRefreshing ? '새로고침...' : '새로고침'}
      </button>
    </div>
  );
};

export default WorkerActiveCountBadge;
